import { useEffect, useRef, useState } from 'react';
import Chart from 'react-apexcharts';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ApexOptions } from 'apexcharts';
import { Box, CardBody, CardHeader, Text } from 'grommet';
import { ColorType } from 'grommet/utils';
import { Card, CardProps, LoadingSpinner } from '/src/components';
import { useDashboardStore } from '/src/context';
import { DashboardData } from '/src/lib/models';
import theme from '/src/theme';
import { standardizeFacilityAddress } from '/src/utils';

export type TFacilitiesData = Record<string, Record<string, number>>;

export const EquipmentByFacilityChart: React.FC<EquipmentByFacilityChartProps> = (props) => {
  /**
   * Props
   */
  const { data, height, ...boxProps } = props;
  const [facilitiesData, setFacilitiesData] = useState<TFacilitiesData>({});
  const [seriesData, setSeriesData] = useState<ApexAxisChartSeries>([]);
  const [orderedFacilityIds, setOrderedFacilityIds] = useState<number[]>([]);

  const chartLabels = Object.keys(facilitiesData);

  const navigate = useNavigate();

  /* Context */
  const dashboardStore = useDashboardStore();
  const { t } = useTranslation();

  /* Refs */
  const clientId = dashboardStore.dashboardData?.client_id;
  const themeColors = useRef(theme.global?.colors ?? {});
  const cardHeight = useRef('31.5rem');
  const chartColors = useRef<ColorType[]>([
    themeColors.current['accent-3'],
    themeColors.current['accent-5'],
    themeColors.current['brand'],
  ]);

  const chartOptions = useRef<ApexOptions>({
    chart: {
      type: 'bar',
      stacked: true,
      fontFamily: 'Roboto Condensed, sans-serif',
      foreColor: themeColors.current['dark-3'] as string,
      animations: {
        enabled: true,
        speed: 500,
        animateGradually: {
          enabled: false,
        },
        dynamicAnimation: {
          speed: 300,
        },
      },
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 6,
        borderRadiusApplication: 'end',
        borderRadiusWhenStacked: 'last',
      },
    },
    dataLabels: {
      enabled: true,
    },
    stroke: {
      show: false,
    },
    colors: chartColors.current,
    grid: {
      borderColor: '#EAECEF',
      strokeDashArray: 4,
      xaxis: {
        lines: { show: true },
      },
      yaxis: {
        lines: { show: false },
      },
      padding: { left: 10, right: 10 },
    },
    xaxis: {
      position: 'top',
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: {
        style: {
          fontWeight: 500,
        },
      },
    },
    yaxis: {
      title: {
        text: undefined,
      },
      labels: {
        style: {
          fontWeight: 500,
          fontSize: '12px',
        },
        maxWidth: 200,
      },
    },
    tooltip: {
      theme: 'light',
      style: {
        fontSize: '13px',
      },
    },
    legend: {
      showForSingleSeries: true,
      showForZeroSeries: false,
      fontSize: '16px',
      position: 'bottom',
      markers: {
        radius: 12,
      },
      itemMargin: {
        horizontal: 8,
        vertical: 4,
      },
    },
  });

  /* Effects */
  useEffect(() => {
    const facilities: TFacilitiesData = {};

    if (!data || !data.facilities) {
      return;
    }

    const dataFacilities = [...data.facilities]
      .sort(
        (a, b) =>
          b.equipment.map((e) => e.amount).reduce((total, amount) => total + amount, 0) -
          a.equipment.map((e) => e.amount).reduce((total, amount) => total + amount, 0)
      )
      .slice(0, 10);

    const newOrderedFacilityIds: number[] = [];

    for (const facility of dataFacilities) {
      if (!facility.id) continue;

      const facilityAddress = `${standardizeFacilityAddress(facility.address.line1)}, ${facility.address.city}, ${
        facility.address.region_short_code
      } ${facility.address.post_code}`;

      facilities[facilityAddress] = {};
      newOrderedFacilityIds.push(facility.id);

      facility.equipment.forEach((equipment) => {
        facilities[facilityAddress][equipment.category] = equipment.amount;
      });
    }

    setFacilitiesData(facilities);
    chartOptions.current = {
      ...chartOptions.current,
      chart: {
        ...chartOptions.current.chart,
        events: {
          dataPointSelection: (_, __, config) => {
            navigate(`/clients/${clientId}/facilities/${newOrderedFacilityIds[config.dataPointIndex]}`);
          },
        },
      },
    };

    const seriesData: ApexAxisChartSeries = [];

    for (const facilityName in facilities) {
      for (const categoryName in facilities[facilityName]) {
        const existingIndex = seriesData.findIndex((s) => s.name === categoryName);

        if (existingIndex > -1) {
          const existingData = seriesData[existingIndex].data as number[];
          existingData.push(facilities[facilityName][categoryName]);
          seriesData[existingIndex].data = existingData;
        } else {
          const newData = {
            name: categoryName,
            data: [facilities[facilityName][categoryName]],
          };
          seriesData.push(newData);
        }
      }
    }

    setSeriesData(
      seriesData.filter((datum) => !!datum.data.reduce((total, amount) => ((total as number) += amount as number)))
    );

    setOrderedFacilityIds(newOrderedFacilityIds);
  }, [data?.facilities, clientId, navigate]);

  /* Render */
  return (
    <Card flex="grow" height={height || cardHeight.current} {...boxProps}>
      <CardHeader>
        <Text size="xlarge">{t('dashboard:equipmentTypeByFacility')}</Text>
      </CardHeader>
      <CardBody pad={{ horizontal: 'medium', bottom: '2rem' }}>
        {!seriesData || !chartLabels ? (
          <LoadingSpinner />
        ) : (
          <Box width="100%" flex="grow" direction="row">
            <Box flex="grow">
              <Chart
                options={{
                  ...chartOptions.current,
                  chart: {
                    ...chartOptions.current.chart,
                    events: {
                      xAxisLabelClick: (_event, _chartContext, { labelIndex }) => {
                        if (clientId && orderedFacilityIds[labelIndex]) {
                          navigate(`/clients/${clientId}/facilities/${orderedFacilityIds[labelIndex]}`);
                        }
                      },
                      dataPointSelection: (_event, _chartContext, { dataPointIndex }) => {
                        if (clientId && orderedFacilityIds[dataPointIndex]) {
                          navigate(`/clients/${clientId}/facilities/${orderedFacilityIds[dataPointIndex]}`);
                        }
                      },
                    },
                  },
                  xaxis: {
                    ...chartOptions.current.xaxis,
                    categories: chartLabels,
                  },
                }}
                series={seriesData}
                type="bar"
                width="100%"
                height="100%"
              />
            </Box>
          </Box>
        )}
      </CardBody>
    </Card>
  );
};

export type EquipmentByFacilityChartProps = CardProps & {
  data: DashboardData;
};
